import React, { useEffect, useState, useMemo } from 'react';
import isEmpty from 'lodash.isempty';
import { Col, Div, Icon, Input, Row, Text } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { Card, FileDrop, Button, ModelsDropdown } from 'src/components';

/* eslint-disable react/prop-types */

export const cardProps = {
  iconProps: {
    name: 'ThinkalikeModel',
    color: 'white',
    size: '3.125rem',
    viewBox: '0 0 45 60',
  },
  titleProps: {
    bg: 'teal',
    h: '5.5rem',
    p: { l: '2.5rem', r: '2.625rem' },
    rounded: { tr: 'md', tl: 'md' },
  },
  titleTextProps: {
    textColor: 'white',
    textSize: 'title',
    lineHeight: 'title',
  },
  cardProps: {
    p: '0',
    textSize: 'paragraph',
    style: {
      lineHeight: '1.5rem',
    },
    rounded: 'md',
    textColor: 'shade66',
  },
};

/*
 * const modelDropdownStyle = {
 *   'data-test': 'target-model-intent-dropdown',
 *   w: '100%',
 *   h: '1.875rem',
 *   m: { y: '.375rem' },
 *   DropdownButtonOptions: {
 *     h: '1.875rem',
 *     w: '1.875rem',
 *     rounded: { tr: 'md', br: 'md' },
 *     border: { y: '1px solid', r: '1px solid', l: '0' },
 *     borderColor: 'shade79',
 *     align: 'center',
 *     justify: 'center',
 *     d: 'flex',
 *     p: '0',
 *     'data-test': 'target-model-intent-dropdown-btn',
 *   },
 *   inputStyle: {
 *     h: 'calc(1.875rem-1px)',
 *     textColor: 'shade99',
 *     flexGrow: '1',
 *     p: { l: '0' },
 *     rounded: 'none',
 *     'data-test': 'target-model-intent-dropdown-input',
 *   },
 *   menuOptions: {
 *     pos: 'absolute',
 *     shadow: 4,
 *     minW: '100%',
 *     rounded: 'md',
 *     maxH: '250px',
 *     bg: 'white',
 *     overflow: 'auto',
 *   },
 *   menuItemOptions: {
 *     d: 'flex',
 *     align: 'center',
 *     p: { x: '1rem', y: '0.5rem' },
 *     hoverBg: 'gray100',
 *     bg: 'white',
 *     'data-test': 'target-model-intent-dropdown-menu-item',
 *   },
 *   DivOptions: {
 *     rounded: { l: 'md', r: '0' },
 *     border: '1px solid',
 *     borderColor: 'shade79',
 *     p: { l: '0.75rem' },
 *   },
 *   openSuffix: <Icon color="shade66" name="UpArrow" size=".625rem" />,
 *   closeSuffix: <Icon color="shade66" name="DownArrow" size=".625rem" />,
 * };
 */

export function TopButtons() {
  return (
    <Row p={{ b: '1.25rem' }}>
      <Col size="12">
        <Div m={{ t: '1rem' }} d="flex" justify="flex-start">
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Target_Onboarding_Template_Updated_18-I-24.xlsx');
            }}
            data-tip="Download Target template"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            TARGET TEMPLATE
          </Button>
        </Div>
      </Col>
    </Row>
  );
}

function BottomButtons({ onBack }) {
  return (
    <Row>
      <Col size="12">
        <Div m={{ t: '1rem', x: '-0.5rem' }} d="flex" justify="space-between">
          <Button
            buttonStyle="primary"
            m={{ l: '0.5rem' }}
            onClick={() => {
              onBack();
            }}
            data-tip="go back"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon color="white" name="LongLeft" size="1rem" m={{ r: '0.5rem' }} />
            BACK
          </Button>
        </Div>
      </Col>
    </Row>
  );
}

function PredictForward({
  datasetTag,
  handleFileDrop,
  handleFileSelect,
  files,
  stageFiles,
  progress,
  uploadFileStatus,
  datasetName,
  isAdmin,
  isSuperAdmin,
  compState,
  triggerDetWorkflowStatus,
  // functions
  setDatasetName,
  getTagStyles,
  uploadFiles,
  setCompState,
  onDone,
  goBack,
  refreshes,
  defaultRefreshId,
  refreshId,
  setRefreshId,
  // model selection (parent-owned Redux state and actions)
  models,
  fetchModelsStatus,
  fetchModelsError,
  modelRequired,
  fetchModels,
  selectedModel,
  setIntendedModel,
}) {
  const [defaultRefresh, setDefaultRefresh] = useState({});
  const [refresh, setRefresh] = useState(/** @type {import('src/store/typings').Refresh} */ ({}));

  // Effective refresh ID derived from the selected targetModelIntent. Prefer the model's refresh
  // ID directly so the upload uses the right refresh even before the refreshes list has loaded.
  const modelDerivedRefreshId = useMemo(
    () => selectedModel?.modelData?.metadataPayload?.refresh?.id ?? defaultRefreshId,
    [selectedModel?.modelData?.metadataPayload?.refresh?.id, defaultRefreshId]
  );

  useEffect(() => {
    if (isEmpty(defaultRefresh))
      // set default refresh
      setDefaultRefresh(refreshes?.find((r) => r.id === defaultRefreshId));
    else if (!refreshId) {
      // preferentially use the model's refresh else default refresh
      if (selectedModel?.modelData?.metadataPayload?.refresh?.id)
        setRefreshId(selectedModel.modelData.metadataPayload.refresh.id);
      else if (defaultRefreshId) setRefreshId(defaultRefreshId);
    } else if (selectedModel?.modelData?.metadataPayload?.refresh?.id !== refreshId) {
      setRefreshId(selectedModel.modelData.metadataPayload.refresh.id);
    }
  }, [
    defaultRefresh,
    defaultRefreshId,
    refresh?.id,
    refreshId,
    refreshes,
    setRefreshId,
    selectedModel?.modelData?.metadataPayload?.refresh?.id,
  ]);

  useEffect(() => {
    if (triggerDetWorkflowStatus === CONSTANTS.API.SUCCEEDED) onDone();
  }, [triggerDetWorkflowStatus, onDone]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  useEffect(() => {
    if (isEmpty(defaultRefresh)) setDefaultRefresh(refreshes?.find((r) => r.name.includes(defaultRefreshId)));
    if (!refreshId) setRefreshId(defaultRefreshId);
    if (refreshId) {
      const matched = refreshes?.find((r) => r.id === refreshId) || refreshes?.find((r) => r.name.includes(refreshId));
      if (matched && matched.id !== refresh?.id) setRefresh(matched);
    }
  }, [defaultRefresh, defaultRefreshId, refresh?.id, refreshId, refreshes, setRefreshId]);

  // When the selected model resolves to a refresh ID, push it directly into Redux so the upload
  // uses the model-derived refresh. The visible refresh object is updated by the effect above
  // when a matching refresh row arrives in the refreshes list.
  useEffect(() => {
    if (modelDerivedRefreshId != null && modelDerivedRefreshId !== refreshId) {
      setRefreshId(modelDerivedRefreshId);
    }
  }, [modelDerivedRefreshId, refreshId, setRefreshId]);

  const isLoadingModels = fetchModelsStatus === CONSTANTS.API.STARTED;
  const isModelFetchUnresolved =
    fetchModelsStatus !== CONSTANTS.API.SUCCEEDED && fetchModelsStatus !== CONSTANTS.API.FAILED;
  const didModelFetchFail = fetchModelsStatus === CONSTANTS.API.FAILED;
  const hasModels = models.length > 0;
  const gatedAwaitingModelSelection = modelRequired && !selectedModel;
  const gatedSelectionMissingRefresh = modelRequired && !!selectedModel && modelDerivedRefreshId == null;
  const gatedSelectionRefreshNotApplied =
    modelRequired && !!selectedModel && modelDerivedRefreshId != null && refreshId !== modelDerivedRefreshId;

  const isUploadDisabled =
    !isAdmin ||
    uploadFileStatus === CONSTANTS.API.STARTED ||
    triggerDetWorkflowStatus === CONSTANTS.API.STARTED ||
    files.length === 0 ||
    !datasetName?.trim() ||
    compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED ||
    datasetName?.trim?.()?.length > 120 ||
    // isModelFetchUnresolved ||
    // gatedAwaitingModelSelection ||
    gatedSelectionMissingRefresh ||
    gatedSelectionRefreshNotApplied;

  return (
    <>
      <Row>
        <Col size="12">
          <TopButtons />
          <Card
            title="Select file to upload"
            iconProps={cardProps.iconProps}
            titleProps={cardProps.titleProps}
            titleTextProps={cardProps.titleTextProps}
            cardProps={cardProps.cardProps}
          >
            <Row p={{ x: '2.5rem', t: '1.75rem', b: '1.875rem' }}>
              <Col size="5">
                <FileDrop
                  onDrop={handleFileDrop}
                  onSelect={handleFileSelect}
                  droppedFiles={files}
                  setDroppedFiles={stageFiles}
                  progress={progress}
                  uploadFileStatus={uploadFileStatus}
                />
              </Col>
              <Col size="7">
                Dataset Name
                <Input
                  value={datasetName}
                  onChange={(e) => {
                    setDatasetName(e.target.value);
                  }}
                  h="1.875rem"
                  m={{ b: '.875rem', t: '.25rem' }}
                  data-test="dataset-name"
                />
                {datasetName?.trim?.()?.length > 120 && (
                  <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                    Dataset name cannot be longer than 120 characters
                  </Text>
                )}
                Type
                <Div
                  align="center"
                  border="1px solid"
                  borderColor="gray500"
                  d="flex"
                  h="1.875rem"
                  m={{ b: '.875rem', t: '.25rem' }}
                  p={{ x: '.75rem' }}
                  rounded="md"
                >
                  <Icon size=".875rem" m={{ r: '.5em' }} alignSelf="center" {...getTagStyles(datasetTag)} />
                  <Text textSize="body" textColor={getTagStyles(datasetTag).color}>
                    {CONSTANTS.SEGMENT.TAG_NAME[datasetTag]}
                  </Text>
                </Div>
                {isSuperAdmin ? (
                  <Div>
                    Refresh
                    <Input
                      data-test="refresh-name"
                      isLoading={!refreshId}
                      value={refresh?.name}
                      disabled
                      h="2.4rem"
                      m={{ b: '14px' }}
                    />
                  </Div>
                ) : null}
                {/*
                 *   modelRequired ? (
                 *   <Div m={{ t: '0.5rem' }} data-test="target-model-intent-section">
                 *     <Text textSize="input" textWeight="500" textColor="shade66">
                 *       Select the intended model
                 *     </Text>
                 *     {didModelFetchFail ? (
                 *       <Text
                 *         textColor="danger800"
                 *         textSize="caption"
                 *         m={{ t: '.375rem' }}
                 *         data-test="target-model-intent-error"
                 *       >
                 *         Could not load eligible models
                 *         {fetchModelsError ? `: ${fetchModelsError}` : '.'} Please refresh the page and try again.
                 *       </Text>
                 *     ) : !isLoadingModels && !hasModels ? (
                 *       <Text
                 *         textColor="shade66"
                 *         textSize="caption"
                 *         m={{ t: '.375rem' }}
                 *         data-test="target-model-intent-empty"
                 *       >
                 *         No eligible models are available. Please create a model before uploading targets.
                 *       </Text>
                 *     ) : (
                 *       <ModelsDropdown
                 *         dropDownStyle={modelDropdownStyle}
                 *         loading={isLoadingModels}
                 *         models={models}
                 *         model={selectedModel}
                 *         defaultText={isLoadingModels ? 'Loading models...' : 'Please select a model'}
                 *         onSelectModel={(nextModel) => setIntendedModel(nextModel ?? null)}
                 *       />
                 *     )}
                 *     {gatedSelectionMissingRefresh ? (
                 *       <Text
                 *         textColor="danger800"
                 *         textSize="caption"
                 *         m={{ t: '.375rem' }}
                 *         data-test="target-model-intent-no-refresh"
                 *       >
                 *         Selected model is missing refresh information; please choose a different model.
                 *       </Text>
                 *     ) : null}
                 *   </Div>
                 * ) : null
                 */}
                <Div m={{ t: '2.5rem' }} d="flex" justify="flex-end">
                  <Button
                    buttonStyle="primary"
                    m={{ r: '0rem' }}
                    isLoading={compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED}
                    disabled={isUploadDisabled}
                    loading={uploadFileStatus === CONSTANTS.API.STARTED}
                    onClick={() => {
                      setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
                      uploadFiles(files, CONSTANTS.S3_PREFIX.ONPOINT_WORKFLOW);
                      setDatasetName(datasetName.trim());
                    }}
                  >
                    UPLOAD
                    <Icon name="UploadForward" viewBox="0 0 14 16" color="white" m={{ l: '0.5rem' }} size="0.875rem" />
                  </Button>
                </Div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <BottomButtons
        onBack={() => {
          goBack();
        }}
      />
    </>
  );
}

export default PredictForward;
